import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, Row, Col, Form, Button, Spinner, Badge } from "react-bootstrap";
import toast from "react-hot-toast";
import { exportData } from "../../../services/exportService";

// ─── Table / column definitions ────────────────────────────────────────────────
const TABLE_CONFIG = [
  {
    label: "Assets",
    tName: "assets",
    icon: "la la-box",
    description: "Export asset inventory data including serial numbers, models, pricing, and status.",
    columns: [
      { key: "sNo", label: "S.No" },
      { key: "serialNumber", label: "Serial Number" },
      { key: "model", label: "Model" },
      { key: "manufacturer", label: "Manufacturer" },
      { key: "purchaseDate", label: "Purchase Date" },
      { key: "originalPrice", label: "Original Price" },
      { key: "currentPrice", label: "Current Price" },
      { key: "status", label: "Status" },
      { key: "createdAt", label: "Created At" },
      { key: "updatedAt", label: "Updated At" },
    ],
  },
  {
    label: "Employees",
    tName: "employees",
    icon: "la la-users",
    description: "Export employee directory data including personal details, contact info and status.",
    columns: [
      { key: "sNo", label: "S.No" },
      { key: "firstName", label: "First Name" },
      { key: "middleName", label: "Middle Name" },
      { key: "lastName", label: "Last Name" },
      { key: "officialEmail", label: "Official Email" },
      { key: "number", label: "Phone Number" },
      { key: "status", label: "Status" },
      { key: "createdAt", label: "Created At" },
      { key: "updatedAt", label: "Updated At" },
    ],
  },
  {
    label: "Salary",
    tName: "salary",
    icon: "la la-money",
    description: "Export salary records including monthly breakdowns, net salary and totals.",
    columns: [
      { key: "sNo", label: "S.No" },
      { key: "month", label: "Month" },
      { key: "employeeSalary", label: "Employee Salary" },
      { key: "totalSalary", label: "Total Salary" },
      { key: "netSalary", label: "Net Salary" },
      { key: "updatedAt", label: "Updated At" },
    ],
  },
  {
    label: "Tasks",
    tName: "tasks",
    icon: "la la-tasks",
    description: "Export task assignments with priorities, deadlines, statuses and assignee details.",
    columns: [
      { key: "sNo", label: "S.No" },
      { key: "title", label: "Title" },
      { key: "description", label: "Description" },
      { key: "taskType", label: "Task Type" },
      { key: "priority", label: "Priority" },
      { key: "deadline", label: "Deadline" },
      { key: "status", label: "Status" },
      { key: "assignedByName", label: "Assigned By" },
      { key: "assignedToName", label: "Assigned To" },
      { key: "taskAssignmentStatus", label: "Task Assignment Status" },
      { key: "createdAt", label: "Created At" },
      { key: "updatedAt", label: "Updated At" },
    ],
  },
  {
    label: "Goals",
    tName: "goals",
    icon: "la la-bullseye",
    description: "Export performance goals with weightage, cycle info, submission and review statuses.",
    columns: [
      { key: "sNo", label: "S.No" },
      { key: "employeeName", label: "Employee Name" },
      { key: "officialEmail", label: "Official Email" },
      { key: "mainGoal", label: "Main Goal" },
      { key: "mainDescription", label: "Main Description" },
      { key: "weightage", label: "Weightage" },
      { key: "year", label: "Year" },
      { key: "cycleName", label: "Cycle" },
      { key: "goalStatusName", label: "Goal Status" },
      { key: "submissionStatusName", label: "Submission Status" },
      { key: "allowEdit", label: "Allow Edit" },
      { key: "selfReviewed", label: "Self Reviewed" },
      { key: "finalPerformanceReviewed", label: "Final Performance Reviewed" },
      { key: "startDate", label: "Start Date" },
      { key: "endDate", label: "End Date" },
      { key: "editDeadline", label: "Edit Deadline" },
      { key: "createdAt", label: "Created At" },
      { key: "updatedAt", label: "Updated At" },
      { key: "status", label: "Status" },
    ],
  },
];

const FORMAT_OPTIONS = [
  { value: "csv", label: "CSV", icon: "la la-file-text", color: "#22c55e" },
  { value: "xlsx", label: "Excel (XLSX)", icon: "la la-file-excel-o", color: "#2563eb" },
  { value: "pdf", label: "PDF", icon: "la la-file-pdf-o", color: "#ef4444" },
];

// ─── Component ──────────────────────────────────────────────────────────────────
const ExportCompany = () => {
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState("csv");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1=table, 2=columns, 3=format+confirm

  // ── helpers ──────────────────────────────────────────────────────────────────
  const handleTableSelect = (table) => {
    setSelectedTable(table);
    setSelectedColumns(table.columns.map((c) => c.key)); // select all by default
    setStep(2);
  };

  const toggleColumn = (key) => {
    setSelectedColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleAllColumns = () => {
    if (!selectedTable) return;
    if (selectedColumns.length === selectedTable.columns.length) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(selectedTable.columns.map((c) => c.key));
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setSelectedTable(null);
      setSelectedColumns([]);
    } else if (step === 3) {
      setStep(2);
    }
  };

  const handleNext = () => {
    if (step === 2) {
      if (selectedColumns.length === 0) {
        toast.error("Please select at least one column.");
        return;
      }
      setStep(3);
    }
  };

  const handleExport = async () => {
    if (!selectedTable || selectedColumns.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        tName: selectedTable.tName,
        cName: selectedColumns,
        format: selectedFormat,
      };
      const response = await exportData(payload);

      // Determine filename from Content-Disposition or build a fallback
      const disposition = response.headers?.["content-disposition"];
      let filename = `${selectedTable.tName}_export.${selectedFormat}`;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Export successful! File "${filename}" downloaded.`);
      // Reset to start
      setStep(1);
      setSelectedTable(null);
      setSelectedColumns([]);
      setSelectedFormat("csv");
    } catch (err) {
      console.error("Export failed", err);
      const status = err?.response?.status;
      if (status === 404) {
        toast.error("No data found to export for the selected criteria.");
      } else if (status === 400) {
        toast.error("Invalid export request. Please check your selections.");
      } else if (status === 401) {
        toast.error("Unauthorized. Please login again.");
      } else {
        toast.error("Export failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const currentTableConfig = selectedTable;

  // ── render helpers ────────────────────────────────────────────────────────────
  const renderStepIndicator = () => (
    <div className="export-steps d-flex align-items-center justify-content-center mb-4">
      {[
        { num: 1, label: "Select Table" },
        { num: 2, label: "Choose Columns" },
        { num: 3, label: "Export" },
      ].map((s, idx) => (
        <React.Fragment key={s.num}>
          <div className="d-flex flex-column align-items-center">
            <div
              className={`step-circle d-flex align-items-center justify-content-center rounded-circle fw-bold`}
              style={{
                width: 40,
                height: 40,
                border: `2px solid ${step >= s.num ? "var(--primary-color, #ff9b44)" : "#dee2e6"}`,
                background: step > s.num
                  ? "var(--primary-color, #ff9b44)"
                  : step === s.num
                    ? "var(--primary-color, #ff9b44)"
                    : "#f8f9fa",
                color: step >= s.num ? "#fff" : "#6c757d",
                transition: "all 0.3s ease",
                fontSize: 14,
              }}
            >
              {step > s.num ? <i className="la la-check" /> : s.num}
            </div>
            <small
              style={{
                marginTop: 4,
                color: step >= s.num ? "var(--primary-color, #ff9b44)" : "#6c757d",
                fontWeight: step === s.num ? 600 : 400,
                fontSize: 12,
              }}
            >
              {s.label}
            </small>
          </div>
          {idx < 2 && (
            <div
              style={{
                flex: 1,
                height: 2,
                background: step > s.num ? "var(--primary-color, #ff9b44)" : "#dee2e6",
                margin: "0 8px",
                marginBottom: 20,
                transition: "background 0.3s ease",
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div>
      <p className="text-muted mb-4 text-center">
        Choose the data category you want to export. Each table contains specific company data.
      </p>
      <Row className="g-3">
        {TABLE_CONFIG.map((table) => (
          <Col md={6} xl={4} key={table.tName}>
            <div
              className="export-table-card p-3 rounded border h-100"
              onClick={() => handleTableSelect(table)}
              style={{
                cursor: "pointer",
                transition: "all 0.25s ease",
                borderColor: "#dee2e6",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--primary-color, #ff9b44)";
                e.currentTarget.style.boxShadow = "0 4px 16px rgba(255,155,68,0.15)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#dee2e6";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div className="d-flex align-items-center mb-2">
                <div
                  className="icon-wrapper d-flex align-items-center justify-content-center rounded me-3"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(255,155,68,0.12)",
                    flexShrink: 0,
                  }}
                >
                  <i className={`${table.icon} text-primary`} style={{ fontSize: 22, color: "var(--primary-color, #ff9b44) !important" }} />
                </div>
                <div>
                  <h6 className="mb-0 fw-bold">{table.label}</h6>
                  <small className="text-muted">{table.columns.length} columns</small>
                </div>
              </div>
              <p className="text-muted mb-2" style={{ fontSize: 13, lineHeight: 1.5 }}>
                {table.description}
              </p>
              <div className="d-flex flex-wrap gap-1">
                {table.columns.slice(0, 4).map((col) => (
                  <Badge
                    key={col.key}
                    bg=""
                    style={{
                      background: "rgba(255,155,68,0.1)",
                      color: "var(--primary-color, #ff9b44)",
                      fontSize: 10,
                      fontWeight: 500,
                    }}
                  >
                    {col.label}
                  </Badge>
                ))}
                {table.columns.length > 4 && (
                  <Badge
                    bg=""
                    style={{
                      background: "#f1f3f5",
                      color: "#6c757d",
                      fontSize: 10,
                    }}
                  >
                    +{table.columns.length - 4} more
                  </Badge>
                )}
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );

  const renderStep2 = () => {
    if (!currentTableConfig) return null;
    const allSelected = selectedColumns.length === currentTableConfig.columns.length;

    return (
      <div>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center justify-content-center rounded"
              style={{ width: 36, height: 36, background: "rgba(255,155,68,0.12)" }}
            >
              <i className={`${currentTableConfig.icon}`} style={{ color: "var(--primary-color, #ff9b44)", fontSize: 18 }} />
            </div>
            <div>
              <h6 className="mb-0 fw-bold">{currentTableConfig.label}</h6>
              <small className="text-muted">
                {selectedColumns.length} / {currentTableConfig.columns.length} columns selected
              </small>
            </div>
          </div>
          <Button
            variant={allSelected ? "outline-secondary" : "outline-primary"}
            size="sm"
            onClick={toggleAllColumns}
          >
            {allSelected ? "Deselect All" : "Select All"}
          </Button>
        </div>

        <div className="column-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "10px" }}>
          {currentTableConfig.columns.map((col) => {
            const checked = selectedColumns.includes(col.key);
            return (
              <div
                key={col.key}
                className="column-item d-flex align-items-center gap-2 p-2 rounded border"
                onClick={() => toggleColumn(col.key)}
                style={{
                  cursor: "pointer",
                  borderColor: checked ? "var(--primary-color, #ff9b44)" : "#dee2e6",
                  background: checked ? "rgba(255,155,68,0.06)" : "#fff",
                  transition: "all 0.2s ease",
                  userSelect: "none",
                }}
              >
                <div
                  className="d-flex align-items-center justify-content-center rounded"
                  style={{
                    width: 20,
                    height: 20,
                    border: `2px solid ${checked ? "var(--primary-color, #ff9b44)" : "#adb5bd"}`,
                    background: checked ? "var(--primary-color, #ff9b44)" : "transparent",
                    flexShrink: 0,
                    transition: "all 0.2s ease",
                  }}
                >
                  {checked && <i className="la la-check" style={{ fontSize: 12, color: "#fff" }} />}
                </div>
                <span style={{ fontSize: 13, fontWeight: checked ? 600 : 400, color: checked ? "#333" : "#555" }}>
                  {col.label}
                </span>
              </div>
            );
          })}
        </div>

        {selectedColumns.length === 0 && (
          <div className="text-center text-danger mt-3" style={{ fontSize: 13 }}>
            <i className="la la-exclamation-circle me-1" />
            Please select at least one column to continue.
          </div>
        )}

        <div className="d-flex justify-content-between mt-4">
          <Button variant="outline-secondary" onClick={handleBack}>
            <i className="la la-arrow-left me-1" /> Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={selectedColumns.length === 0}
            style={{ background: "var(--primary-color, #ff9b44)", border: "none" }}
          >
            Next <i className="la la-arrow-right ms-1" />
          </Button>
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    if (!currentTableConfig) return null;
    const selectedFmt = FORMAT_OPTIONS.find((f) => f.value === selectedFormat);

    return (
      <div>
        {/* Format Selection */}
        <h6 className="fw-bold mb-3">Select Export Format</h6>
        <Row className="g-3 mb-4">
          {FORMAT_OPTIONS.map((fmt) => (
            <Col md={4} key={fmt.value}>
              <div
                onClick={() => setSelectedFormat(fmt.value)}
                className="d-flex flex-column align-items-center justify-content-center p-3 rounded border text-center"
                style={{
                  cursor: "pointer",
                  borderColor: selectedFormat === fmt.value ? fmt.color : "#dee2e6",
                  background: selectedFormat === fmt.value ? `${fmt.color}10` : "#fff",
                  transition: "all 0.2s ease",
                  minHeight: 100,
                }}
              >
                <i
                  className={fmt.icon}
                  style={{ fontSize: 32, color: selectedFormat === fmt.value ? fmt.color : "#adb5bd", marginBottom: 8 }}
                />
                <span style={{ fontWeight: selectedFormat === fmt.value ? 700 : 400, color: selectedFormat === fmt.value ? fmt.color : "#555", fontSize: 14 }}>
                  {fmt.label}
                </span>

              </div>
            </Col>
          ))}
        </Row>

        {/* Summary */}
        <div className="export-summary p-3 rounded" style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}>
          <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
            <i className="la la-info-circle text-primary" />
            Export Summary
          </h6>
          <Row>
            <Col md={4} className="mb-2">
              <div className="text-muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Table</div>
              <div className="fw-semibold d-flex align-items-center gap-1 mt-1">
                <i className={`${currentTableConfig.icon}`} style={{ color: "var(--primary-color, #ff9b44)" }} />
                {currentTableConfig.label}
              </div>
            </Col>
            <Col md={4} className="mb-2">
              <div className="text-muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Columns</div>
              <div className="fw-semibold mt-1">{selectedColumns.length} selected</div>
            </Col>
            <Col md={4} className="mb-2">
              <div className="text-muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Format</div>
              <div className="fw-semibold mt-1 d-flex align-items-center gap-1">
                <i className={selectedFmt?.icon} style={{ color: selectedFmt?.color }} />
                {selectedFmt?.label}
              </div>
            </Col>
          </Row>
          <hr style={{ margin: "12px 0" }} />
          <div>
            <div className="text-muted mb-2" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Selected Columns</div>
            <div className="d-flex flex-wrap gap-1">
              {selectedColumns.map((key) => {
                const col = currentTableConfig.columns.find((c) => c.key === key);
                return (
                  <Badge
                    key={key}
                    bg=""
                    style={{
                      background: "rgba(255,155,68,0.12)",
                      color: "var(--primary-color, #ff9b44)",
                      fontSize: 11,
                      fontWeight: 500,
                    }}
                  >
                    {col?.label || key}
                  </Badge>
                );
              })}
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between mt-4">
          <Button variant="outline-secondary" onClick={handleBack} disabled={loading}>
            <i className="la la-arrow-left me-1" /> Back
          </Button>

          <Button
            onClick={loading ? undefined : handleExport}
            disabled={loading ? true : false}
            style={{
              background: "var(--primary-color, #ff9b44)",
              border: "none",
              minWidth: 140,
              opacity: 1,
              pointerEvents: loading ? "none" : "auto",
              cursor: loading ? "not-allowed" : "pointer",
              color: "#fff",
            }}
          >
            {loading ? <><i className="la la-spinner la-spin me-1" /> Exporting...</> : <><i className="la la-download me-1" /> Export Now</>}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Page Header */}
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h3 className="page-title">Export Company Data</h3>
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="../employee-dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">CRM</li>
                <li className="breadcrumb-item active">Export Company Data</li>
              </ul>
            </div>
            <div className="col-md-6 text-end">
              <div className="d-flex align-items-center justify-content-end gap-2">
                <i className="la la-info-circle text-muted" style={{ fontSize: 16 }} />
                <small className="text-muted">
                  Exports are generated in real-time from the latest company data.
                </small>
              </div>
            </div>
          </div>
        </div>
        {/* /Page Header */}

        {/* Quick-info banner */}
        <div
          className="d-flex align-items-center gap-3 p-3 rounded mb-4"
          style={{
            background: "linear-gradient(135deg, rgba(255,155,68,0.12) 0%, rgba(255,155,68,0.04) 100%)",
            border: "1px solid rgba(255,155,68,0.25)",
          }}
        >
          <i className="la la-download" style={{ fontSize: 32, color: "var(--primary-color, #ff9b44)" }} />
          <div>
            <h6 className="mb-0 fw-bold">Dynamic Data Export</h6>
            <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
              Select a data table, pick the columns you need, choose your format and download instantly.
              Supports <strong>CSV</strong>, <strong>Excel (XLSX)</strong> and <strong>PDF</strong>.
            </p>
          </div>
        </div>

        <Card className="shadow-sm border-0">
          <Card.Body className="p-4">
            {renderStepIndicator()}

            <hr className="mb-4" />

            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default ExportCompany;
